// Super_admin-only top-level page for onboarding a brand-new station.
// Lives at /admin/onboard. Pulled OUT of AdminShell because onboarding
// creates a new station and isn't scoped to any current one.
//
// Auth:
//   • signed out                       → redirect to /admin (single sign-in surface)
//   • signed in, not super_admin       → redirect to /admin (picker)
//   • signed in, super_admin           → render the onboarding form
//
// On successful onboard, navigate to /admin/<newslug> so the user drops
// into the freshly-created station's admin shell.

import React from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, Loader2, Sparkles } from 'lucide-react';
import { rememberedAdminStation } from '../../components/self-service/admin/lastAdminStation';
import PublisherAdminLayout from '../../components/self-service/admin/PublisherAdminLayout';
import { fetchPublisherConfig } from '../../data/publisherConfigs';
import type { PublisherConfig } from '../../types/selfService';
import PublisherForm, {
  type FormSectionProgress,
  type PublisherFormPayload,
} from '../../components/self-service/admin/PublisherForm';
import { usePublisherFormSubmit } from '../../components/self-service/admin/usePublisherFormSubmit';
import PublisherIntakeLinkCreator from '../../components/self-service/admin/PublisherIntakeLinkCreator';
import PublisherIntakeTemplatePicker from '../../components/self-service/admin/PublisherIntakeTemplatePicker';
import {
  intakeClipsToTimeline,
  publisherIntakeToConfig,
} from '../../components/self-service/admin/publisherIntakeToConfig';
import { useAuth } from '../../components/self-service/auth/useAuth';
import { apiClient } from '../../lib/apiClient';
import type { PublisherIntakeSubmission } from '../../types/publisherIntake';

const PublisherOnboardPage: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const submitCreate = usePublisherFormSubmit('selfservice-create-publisher');
  const [template, setTemplate] = React.useState<PublisherIntakeSubmission | null>(null);
  const [progress, setProgress] = React.useState<FormSectionProgress[]>([]);
  // MUST be reference-stable across re-renders: PublisherForm's initial-sync
  // effect resets ALL form state when `initial` changes identity. Building
  // this inline meant any page re-render (e.g. the tracker's setProgress
  // after a section flipped) silently wiped the admin's in-progress edits —
  // the "my market row vanished when I picked a DMA" bug.
  const initialConfig = React.useMemo(
    () => (template ? publisherIntakeToConfig(template) : null),
    [template],
  );
  const initialClips = React.useMemo(
    () => (template ? intakeClipsToTimeline(template) : undefined),
    [template],
  );
  const [organizations, setOrganizations] = React.useState<{ id: string; name: string }[]>([]);

  const isSuperAdmin = isAuthenticated && user?.rawRole === 'super_admin';

  React.useEffect(() => {
    if (!isSuperAdmin) return;
    let cancelled = false;
    (async () => {
      try {
        const rows = await apiClient.get<{ id: string; name: string }[]>('/admin/organizations');
        if (!cancelled) setOrganizations(rows ?? []);
      } catch {
        // The picker degrades to "no organizations"; the form loads its own list.
      }
    })();
    return () => { cancelled = true; };
  }, [isSuperAdmin]);

  if (!isAuthenticated || !user) {
    // Single sign-in surface lives at /admin. Carry the deep-link target
    // so AdminEntryPage can route the user back here after auth completes.
    return (
      <Navigate
        to="/admin"
        state={{ from: location.pathname + location.search }}
        replace
      />
    );
  }

  if (user.rawRole !== 'super_admin') {
    return <Navigate to="/admin" replace />;
  }

  const handleCreate = async (payload: PublisherFormPayload) => {
    // The server pins the organization from the intake row and CAS-consumes it
    // in the same transaction that creates the station.
    const result = await submitCreate({
      ...payload,
      ...(template ? { intakeSubmissionId: template.id } : {}),
    });
    setTimeout(() => navigate(`/admin/${result.slug}`), 600);
    return result;
  };

  return (
    <div
      className={
        embedded
          ? ''
          : 'min-h-screen bg-gradient-to-b from-slate-300 via-slate-200 to-slate-300'
      }
    >
      <div
        className={
          embedded
            ? 'max-w-6xl'
            : 'max-w-6xl mx-auto px-4 sm:px-6 md:px-8 py-10 md:py-14'
        }
      >
        {!embedded && (
          <Link
            to="/admin"
            className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-4"
          >
            <ArrowLeft className="w-4 h-4" /> Back to admin
          </Link>
        )}
        <div className="mb-8">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-900 text-white text-[10px] font-semibold uppercase tracking-[0.14em] mb-4">
            <Sparkles className="w-3 h-3" />
            New channel
          </div>
          <h1 className="text-[28px] md:text-[36px] font-semibold tracking-tight text-slate-900 leading-tight">
            Onboard a channel
          </h1>
          <p className="mt-3 text-[15px] text-slate-500 leading-relaxed max-w-xl">
            Paste the channel's website URL and click{' '}
            <span className="font-medium text-slate-700">Extract branding</span> to pre-fill
            the logo, colors, font, and metro. Review the rest before saving.
          </p>
        </div>
        {/* Start of the loop: send a publisher a link. Sits above the picker
            because it comes first and is usually days apart from finalizing. */}
        <div className="mb-6">
          <PublisherIntakeLinkCreator organizations={organizations} />
        </div>
        <div className="mb-5">
          <PublisherIntakeTemplatePicker
            selectedId={template?.id ?? null}
            onSelect={setTemplate}
          />
        </div>
        {/* Remount on template change: the form's `initial` effect seeds most
            state but NOT organizationId or backgroundClips, so without a fresh
            mount a second template would inherit the first one's org and clips. */}
        <div className="lg:grid lg:grid-cols-[1fr,280px] lg:gap-8 lg:items-start">
          <div className="min-w-0">
            <PublisherForm
              key={template?.id ?? 'blank'}
              mode="create"
              initial={initialConfig}
              initialBackgroundClips={initialClips}
              sourceLabel={template ? (template.stationName ?? template.organizationName) : null}
              organizationLocked={Boolean(template)}
              onSubmit={handleCreate}
              onSectionProgress={setProgress}
            />
          </div>
          {/* Same tracker idiom as the publisher intake's rail. */}
          <aside className="hidden lg:block lg:sticky lg:top-8">
            {progress.length > 0 && (
              <div
                className="rounded-2xl border border-slate-200/80 bg-white p-5"
                style={{ boxShadow: '0 1px 2px rgba(15,23,42,0.08), 0 18px 40px -16px rgba(15,23,42,0.28)' }}
              >
                <p className="text-[12px] font-medium text-slate-600">
                  {progress.filter((sec) => sec.done && !sec.optional).length} of{' '}
                  {progress.filter((sec) => !sec.optional).length} required sections complete
                </p>
                <div className="mt-2 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-slate-900 transition-all duration-300"
                    style={{
                      width: `${(progress.filter((sec) => sec.done && !sec.optional).length
                        / Math.max(progress.filter((sec) => !sec.optional).length, 1)) * 100}%`,
                    }}
                  />
                </div>
                <ul className="mt-4 space-y-1">
                  {progress.map((sec) => (
                    <li key={sec.id}>
                      <button
                        type="button"
                        onClick={() => {
                          const el = document.getElementById(sec.id);
                          if (typeof el?.scrollIntoView === 'function') {
                            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }
                        }}
                        className="w-full flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-left hover:bg-slate-50 transition-colors"
                      >
                        {sec.done ? (
                          <span className="w-[18px] h-[18px] rounded-full flex items-center justify-center shrink-0 bg-emerald-500">
                            <Check className="w-3 h-3 text-white" />
                          </span>
                        ) : (
                          <span className="w-[18px] h-[18px] rounded-full border-2 border-slate-200 shrink-0" />
                        )}
                        <span className={`text-[12.5px] ${sec.done ? 'text-slate-900 font-medium' : 'text-slate-600'}`}>
                          {sec.title}
                        </span>
                        {sec.optional && !sec.done && (
                          <span className="ml-auto text-[10px] font-medium uppercase tracking-wide text-slate-400">
                            Optional
                          </span>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
};

/**
 * /admin/onboard entry. If this session has visited a station dashboard, wrap
 * the page in that station's sidebar so the tabs stay one click away — the
 * remembered station is NAVIGATION CHROME ONLY and has no effect on the
 * onboarding itself (the page is org-agnostic; the owning org still comes
 * from the template or the form's own picker). Cold direct visits render the
 * standalone chrome instead.
 */
const PublisherOnboardEntry: React.FC = () => {
  const { user } = useAuth();
  const rememberedSlug = React.useMemo(() => rememberedAdminStation(), []);
  const [shellConfig, setShellConfig] = React.useState<PublisherConfig | null | 'loading'>(
    rememberedSlug ? 'loading' : null,
  );
  React.useEffect(() => {
    if (!rememberedSlug) return;
    let cancelled = false;
    fetchPublisherConfig(rememberedSlug)
      .then((config) => { if (!cancelled) setShellConfig(config); })
      .catch(() => { if (!cancelled) setShellConfig(null); });
    return () => { cancelled = true; };
  }, [rememberedSlug]);

  if (shellConfig === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-5 h-5 animate-spin text-slate-500" />
      </div>
    );
  }
  if (!shellConfig) return <PublisherOnboardPage />;
  // CTV-759: the layout takes an AdminContext now. This route is outside the
  // admin shell (it has no outlet context), so it synthesizes a single-station
  // one from the remembered station — which is exactly what the chrome is.
  // rememberAdminStation never stores the `all` sentinel, so this can't
  // accidentally resolve the sentinel as a slug.
  return (
    <PublisherAdminLayout
      context={{
        mode: 'single',
        config: shellConfig,
        slug: shellConfig.slug,
        user: { id: user?.id ?? '', email: user?.email ?? '', rawRole: user?.rawRole },
      }}
    >
      <PublisherOnboardPage embedded />
    </PublisherAdminLayout>
  );
};

export default PublisherOnboardEntry;
